import { AI_PAYLOAD_STRING_KEYS, type CreateLlmDbAiPayload } from './types'
import { BASIC_TENANT_ID } from './constants'

export function coerceLlmSettingValueForApi(raw: string): string | number {
  const t = String(raw ?? '').trim()
  if (t === '') return ''
  const n = Number(t)
  if (!Number.isNaN(n) && String(n) === t) return n
  return t
}

export function isCreateLlmDbAiPayload(value: unknown): value is CreateLlmDbAiPayload {
  if (value === null || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  for (const k of AI_PAYLOAD_STRING_KEYS) {
    if (typeof v[k] !== 'string') return false
  }
  if (typeof v.isTenantAdmin !== 'boolean') return false
  if (!Array.isArray(v.tenantIds) || !v.tenantIds.every((x) => typeof x === 'string')) return false
  if (!Array.isArray(v.llmParameters)) return false
  for (const item of v.llmParameters) {
    if (item === null || typeof item !== 'object') return false
    const p = item as Record<string, unknown>
    if (typeof p.name !== 'string' || typeof p.value !== 'string') return false
  }
  if (v.id !== undefined && typeof v.id !== 'string') return false
  return true
}

/** Maps the form's AI payload to the LLM config API body (snake_case). */
export function buildLlmConfigApiPayload(form: CreateLlmDbAiPayload): Record<string, any> {
  const llm_settings = (form.llmParameters ?? [])
    .filter((p) => p?.name?.trim())
    .map((p) => ({
      key: String(p.name).trim(),
      value: coerceLlmSettingValueForApi(String(p.value ?? '')),
    }))

  const isUpdate = Boolean(form?.id)
  const isTenantAdmin = Boolean(form?.isTenantAdmin)
  const loggedTenantId = String(form?.loggedTenantId ?? '').trim()
  const tenantScope = String(form?.tenantScope ?? '').trim()
  const selectedTenantIds: string[] = Array.isArray(form?.tenantIds)
    ? form.tenantIds.map(String).map((s) => s.trim()).filter(Boolean)
    : []

  const resolveTenantIdArray = (): string[] => {
    if (!isTenantAdmin) return loggedTenantId ? [loggedTenantId] : []
    if (tenantScope === BASIC_TENANT_ID) return [BASIC_TENANT_ID]
    if (isUpdate) return selectedTenantIds.length ? [selectedTenantIds[0]] : []
    return selectedTenantIds
  }

  const tenantIdsResolved = resolveTenantIdArray()
  const tenant_id = isUpdate ? (tenantIdsResolved[0] ?? '') : tenantIdsResolved

  const body: Record<string, any> = {
    display_name: form.displayName,
    system_version: form.systemVersion,
    status: form.status,
    model_type: form.modelType,
    llm_type: form.llmType,
    deployment_name: form.deploymentName,
    model_name: form.modelName,
    api_type: form.apiType,
    api_baseurl: form.apiBaseUrl,
    api_version: form.apiVersion,
    api_secret_key: form.apiSecretKey,
    api_secret_value: form.apiSecretValue,
    fn_calling_enabled: form.functionCallingEnabled === 'true',
    llm_settings,
    tenant_id,
    product_id: String(form.productId ?? '').trim(),
  }

  if (form.id) body.id = form.id

  return body
}
